/* HealthIntelligencePage — личен health graph dashboard (iter420 Phase 1 MVP).
 *
 * Структура:
 *   1. KPI Hero — 4 големи карти (DIL avg / NIS avg / Тегло / Талия + latest VAIS)
 *   2. Snapshot timeline — newest first list на всички запазени snapshots
 *   3. Empty state — насочваща ваковата покана когато няма данни
 *
 * iter420 Phase 1 покрива:
 *   - GET /api/health-intelligence/summary
 *   - GET /api/health-intelligence/snapshots
 *   - DELETE /api/health-intelligence/snapshot/{id}
 *
 * Бъдещи фази (P2-P4):
 *   - Любими храни + Trends (Phase 2)
 *   - Заболявания/симптоми/фамилна история (Phase 3)
 *   - AI Insights / Connection Engine (Phase 4)
 */
import MedsSummaryStrip from "@/components/meds/MedsSummaryStrip";
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { twinLabel } from "@/lib/twinCatalog";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import HealthHubCompass from "@/components/HealthHubCompass";
import { MobileTopBar } from "@/components/MobileTopBar";
import {
  Brain, Activity, Flame, Scale, Ruler, FlaskConical,
  Bookmark, Trash2, Loader2, Sparkles, AlertCircle, ArrowRight,
  TrendingDown, TrendingUp, Minus,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import HowToStartCard from "@/components/HowToStartCard";  // iter504 — guided intro
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { bg as bgLocale, enUS } from "date-fns/locale";
import HealthIntelligenceScoreRing from "@/components/HealthIntelligenceScoreRing";
import MealHistoryTrends from "@/components/MealHistoryTrends";
import FavoriteFoodsList from "@/components/FavoriteFoodsList";
import FoodLabRecommendedTile from "@/components/health_intelligence/FoodLabRecommendedTile";  // iter462 Phase 6
import Col3TrackerTile from "@/components/health_intelligence/Col3TrackerTile";              // iter465 Phase B
import PersonalHealthManager from "@/components/PersonalHealthManager";
import WeeklyReportCard from "@/components/WeeklyReportCard";
import ShareWithDoctorCard from "@/components/ShareWithDoctorCard";
import BodyMeasurementsCard from "@/components/BodyMeasurementsCard";
import AggregatedHealthScoresCard from "@/components/AggregatedHealthScoresCard";
import LifestyleTracker from "@/components/LifestyleTracker";
import AIConnectionEngineCard from "@/components/AIConnectionEngineCard";
import PersonalizedMenuCard from "@/components/PersonalizedMenuCard";
import NutritionDilFoodOptimizerCard from "@/components/NutritionDilFoodOptimizerCard";
import MealComposerCard from "@/components/MealComposerCard";
import CycleAwareCard from "@/components/CycleAwareCard";
import RestaurantModeCard from "@/components/RestaurantModeCard";
import FamilyMealCard from "@/components/FamilyMealCard";
import MetabolicPathwaysCard from "@/components/MetabolicPathwaysCard";
import LactationCoachEntryCard from "@/components/lactation/LactationCoachEntryCard";  // iter460
import QuickLabEntryCard from "@/components/QuickLabEntryCard";
import SaveToPmosButton from "@/components/womens_health/SaveToPmosButton";
import CollapsibleSection from "@/components/CollapsibleSection";
import HealthIntelligenceConsentModal from "@/components/health_intelligence/HealthIntelligenceConsentModal";  // iter485 — explicit GDPR consent
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LayoutDashboard, HeartPulse, Utensils, User, Smile } from "lucide-react";
import BhiSnapshotsTab from "@/components/health_intelligence/BhiSnapshotsTab";

const SNAPSHOT_TYPE_META = {
  meal_plate:    { icon: Flame,        bg: "Анализ на чиния",  en: "Plate analysis",     color: "from-orange-500 to-amber-500" },
  lab_panel:     { icon: FlaskConical, bg: "Лабораторен панел", en: "Lab panel",          color: "from-sky-500 to-cyan-500" },
  vais:          { icon: Scale,        bg: "VAIS snapshot",    en: "VAIS snapshot",      color: "from-rose-500 to-pink-500" },
  digital_twin:  { icon: Brain,        bg: "Digital Twin",     en: "Digital Twin",       color: "from-violet-500 to-fuchsia-500" },
  daily_summary: { icon: Activity,     bg: "Дневен преглед",   en: "Daily summary",      color: "from-emerald-500 to-teal-500" },
  supplement:    { icon: Sparkles,     bg: "Добавка",          en: "Supplement",         color: "from-indigo-500 to-violet-500" },
  favorite_food: { icon: Bookmark,     bg: "Любима храна",     en: "Favorite food",      color: "from-amber-500 to-orange-500" },
};

function KpiCard({ icon: Icon, label, value, unit, color, isDark, hint, testId, tooltip, computeTo, computeLabel }) {
  const showCompute = (value == null || value === "" || value === 0) && computeTo;
  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"} overflow-hidden ${tooltip ? "cursor-help" : ""}`}
          data-testid={testId}
          title={tooltip || undefined}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br ${color} text-white`}>
            <Icon className="w-4.5 h-4.5" />
          </div>
          <div className={`text-[11px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {label}
          </div>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className={`text-3xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {value == null ? "—" : value}
          </span>
          {unit && value != null && (
            <span className={`text-xs font-medium ${isDark ? "text-slate-500" : "text-slate-500"}`}>{unit}</span>
          )}
        </div>
        {hint && !showCompute && (
          <p className={`text-[11px] mt-1.5 ${isDark ? "text-slate-500" : "text-slate-500"}`}>{hint}</p>
        )}
        {showCompute && (
          <Link to={computeTo} data-testid={`${testId}-compute-btn`}>
            <Button
              variant="outline"
              size="sm"
              className={`mt-2 h-7 text-[10.5px] font-bold ${
                isDark
                  ? "border-violet-500/40 text-violet-200 hover:bg-violet-500/15"
                  : "border-violet-300 text-violet-700 hover:bg-violet-50"
              }`}
            >
              {computeLabel || "Изчисли"}
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

function SnapshotRow({ snap, isDark, bg, locale, onDelete, isFemale }) {
  const meta = SNAPSHOT_TYPE_META[snap.snapshot_type] || SNAPSHOT_TYPE_META.daily_summary;
  const Icon = meta.icon;
  const when = snap.created_at ? format(parseISO(snap.created_at), "dd MMM yyyy · HH:mm", { locale }) : "";
  const [expanded, setExpanded] = React.useState(false);
  const payload = snap.payload || {};

  // Detailed line-items за meal_plate / favorite_food / lab_panel
  const renderDetails = () => {
    if (snap.snapshot_type === "meal_plate") {
      const foods = payload.foods_detailed || payload.foods || [];
      const pi = payload.pattern_indices || {};
      return (
        <div className="space-y-2 mt-2">
          {/* Macros chips */}
          <div className="flex flex-wrap gap-1.5">
            {payload.dil != null && <Chip isDark={isDark} label="DIL" value={`${Number(payload.dil).toFixed(1)}g`} tone="orange" />}
            {payload.nis_pct != null && <Chip isDark={isDark} label="NIS" value={`${Number(payload.nis_pct).toFixed(0)}%`} tone="violet" />}
            {payload.uis_pct != null && <Chip isDark={isDark} label={bg ? "Възпаление" : "UIS"} value={`${Number(payload.uis_pct).toFixed(0)}%`} tone="amber" />}
            {payload.satiety != null && <Chip isDark={isDark} label={bg ? "Ситост" : "Satiety"} value={`${Number(payload.satiety).toFixed(0)}/100`} tone="emerald" />}
            {payload.glycemic_load != null && <Chip isDark={isDark} label="GL" value={Number(payload.glycemic_load).toFixed(1)} tone="orange" />}
            {payload.total_calories != null && <Chip isDark={isDark} label="kcal" value={Math.round(payload.total_calories)} tone="slate" />}
            {payload.protein_g != null && <Chip isDark={isDark} label="P" value={`${Math.round(payload.protein_g)}g`} tone="slate" />}
            {payload.carbs_g != null && <Chip isDark={isDark} label="C" value={`${Math.round(payload.carbs_g)}g`} tone="slate" />}
            {payload.fat_g != null && <Chip isDark={isDark} label="F" value={`${Math.round(payload.fat_g)}g`} tone="slate" />}
            {payload.fiber_g != null && <Chip isDark={isDark} label={bg ? "Фибри" : "Fiber"} value={`${Math.round(payload.fiber_g)}g`} tone="emerald" />}
          </div>
          {/* Pattern indices (EDIH/EDIP/DII) */}
          {(pi.edih != null || pi.edip != null || pi.dii != null) && (
            <div className="flex flex-wrap gap-1.5">
              {pi.edih != null && <Chip isDark={isDark} label="EDIH" value={Number(pi.edih).toFixed(0)} tone="violet" />}
              {pi.edip != null && <Chip isDark={isDark} label="EDIP" value={Number(pi.edip).toFixed(0)} tone="amber" />}
              {pi.dii  != null && <Chip isDark={isDark} label="DII"  value={Number(pi.dii).toFixed(2)} tone="amber" />}
            </div>
          )}
          {/* Extended Plate metrics */}
          {(payload.incretin || payload.hepatic_stress || payload.dopamine_load || payload.metabolic_flexibility || payload.longevity || payload.col3_synthesis || payload.female_endocrine || payload.circadian) && (
            <div className={`grid grid-cols-2 gap-1.5 text-[10.5px] ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              {payload.incretin?.percent != null && (
                <div>🍯 {bg ? "Инкретин" : "Incretin"}: <strong>{Math.round(payload.incretin.percent)}%</strong></div>
              )}
              {payload.hepatic_stress?.percent != null && (
                <div>🫀 {bg ? "Чернодробен стрес" : "Hepatic stress"}: <strong>{Math.round(payload.hepatic_stress.percent)}%</strong></div>
              )}
              {payload.dopamine_load?.percent != null && (
                <div>🧠 {bg ? "Допамин" : "Dopamine"}: <strong>{Math.round(payload.dopamine_load.percent)}%</strong></div>
              )}
              {payload.metabolic_flexibility?.percent != null && (
                <div>⚡ {bg ? "Метаб. гъвкавост" : "Met. flex"}: <strong>{Math.round(payload.metabolic_flexibility.percent)}%</strong></div>
              )}
              {payload.longevity?.score != null && (
                <div>⏳ {bg ? "Дълголетие" : "Longevity"}: <strong>{Math.round(payload.longevity.score)}/100</strong></div>
              )}
              {payload.col3_synthesis?.score != null && (
                <div>🧬 Col3: <strong>{Math.round(payload.col3_synthesis.score)}/100</strong></div>
              )}
              {isFemale && payload.female_endocrine?.phase_bg && (
                <div>🌙 {bg ? "Фаза" : "Phase"}: <strong>{payload.female_endocrine.phase_bg}</strong></div>
              )}
              {payload.circadian?.adapted_il != null && (
                <div>🌅 {bg ? "Циркаден IL" : "Circ. IL"}: <strong>{Number(payload.circadian.adapted_il).toFixed(1)}</strong></div>
              )}
            </div>
          )}
          {/* Foods list */}
          {Array.isArray(foods) && foods.length > 0 && (
            <div className={`text-[11px] ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              <strong>{bg ? "Храни в чинията:" : "Foods on plate:"}</strong>
              <ul className="mt-1 space-y-0.5 pl-3">
                {foods.slice(0, 12).map((f, i) => {
                  if (typeof f === "string") return <li key={i}>• {f}</li>;
                  return <li key={i}>• {f.name}{f.grams ? ` — ${Math.round(f.grams)}g` : ""}</li>;
                })}
              </ul>
            </div>
          )}
        </div>
      );
    }
    if (snap.snapshot_type === "favorite_food") {
      return (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {payload.dil != null && <Chip isDark={isDark} label="DIL" value={`${Number(payload.dil).toFixed(1)}g`} tone="orange" />}
          {payload.protein_g != null && <Chip isDark={isDark} label="P" value={`${Math.round(payload.protein_g)}g`} tone="slate" />}
          {payload.carbs_g != null && <Chip isDark={isDark} label="C" value={`${Math.round(payload.carbs_g)}g`} tone="slate" />}
          {payload.fat_g != null && <Chip isDark={isDark} label="F" value={`${Math.round(payload.fat_g)}g`} tone="slate" />}
          {payload.fiber_g != null && <Chip isDark={isDark} label={bg ? "Фибри" : "Fiber"} value={`${Math.round(payload.fiber_g)}g`} tone="emerald" />}
          {payload.calories != null && <Chip isDark={isDark} label="kcal" value={Math.round(payload.calories)} tone="slate" />}
        </div>
      );
    }
    if (snap.snapshot_type === "lab_panel") {
      const bm = payload.biomarkers || [];
      if (!Array.isArray(bm) || bm.length === 0) return null;
      return (
        <div className={`mt-2 space-y-1 text-[11px] ${isDark ? "text-slate-300" : "text-slate-700"}`}>
          {bm.slice(0, 8).map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <strong className="flex-1 truncate">{b.name}</strong>
              <span className="font-mono">{b.value} {b.unit}</span>
            </div>
          ))}
          {bm.length > 8 && (
            <div className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>
              + още {bm.length - 8}…
            </div>
          )}
        </div>
      );
    }
    // Generic — show first 6 payload key/values
    const entries = Object.entries(payload).filter(([k, v]) =>
      !["source", "version", "foods", "foods_detailed", "biomarkers"].includes(k) &&
      (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    );
    if (entries.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {entries.slice(0, 8).map(([k, v]) => (
          <Chip key={k} isDark={isDark} label={k} value={String(v).slice(0, 24)} tone="slate" />
        ))}
      </div>
    );
  };

  const hasDetail = (payload && Object.keys(payload).length > 0);

  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/40 hover:bg-slate-900/60" : "border-slate-200 bg-white hover:bg-slate-50/50"} transition-colors`}
          data-testid={`hi-snapshot-${snap.id}`}>
      <CardContent className="p-3.5">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center bg-gradient-to-br ${meta.color} text-white`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h4 className={`text-sm font-bold truncate ${isDark ? "text-white" : "text-slate-900"}`}>
                  {snap.title}
                </h4>
                <p className={`text-[11px] mt-0.5 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                  {bg ? meta.bg : meta.en} · {when}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {hasDetail && (
                  <button
                    onClick={() => setExpanded((x) => !x)}
                    className={`text-[10px] px-2 py-1 rounded font-bold ${
                      isDark ? "text-violet-300 hover:bg-violet-500/10" : "text-violet-700 hover:bg-violet-50"
                    }`}
                    data-testid={`hi-snapshot-toggle-${snap.id}`}
                  >
                    {expanded ? (bg ? "Скрий" : "Hide") : (bg ? "Детайли" : "Details")}
                  </button>
                )}
                <Button
                  onClick={() => onDelete(snap.id)}
                  variant="ghost"
                  size="icon"
                  className={`h-7 w-7 flex-shrink-0 ${isDark ? "text-slate-500 hover:text-rose-300 hover:bg-rose-500/10" : "text-slate-400 hover:text-rose-600 hover:bg-rose-50"}`}
                  data-testid={`hi-snapshot-delete-${snap.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            {snap.summary && (
              <p className={`text-xs mt-2 leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {snap.summary}
              </p>
            )}
            {expanded && renderDetails()}
            {snap.tags && snap.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {snap.tags.slice(0, 6).map((t) => (
                  <span key={t}
                        className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          isDark ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-600"
                        }`}>
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const CHIP_TONES = {
  orange:  { dark: "bg-orange-500/15 text-orange-200 border-orange-500/30",   light: "bg-orange-50 text-orange-700 border-orange-200" },
  violet:  { dark: "bg-violet-500/15 text-violet-200 border-violet-500/30",   light: "bg-violet-50 text-violet-700 border-violet-200" },
  amber:   { dark: "bg-amber-500/15 text-amber-200 border-amber-500/30",      light: "bg-amber-50 text-amber-700 border-amber-200" },
  emerald: { dark: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30", light: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  slate:   { dark: "bg-slate-800/80 text-slate-300 border-slate-700",         light: "bg-slate-100 text-slate-700 border-slate-200" },
};

function Chip({ isDark, label, value, tone = "slate" }) {
  const t = CHIP_TONES[tone] || CHIP_TONES.slate;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${
          isDark ? t.dark : t.light
        }`}>
      <span className="uppercase tracking-wider text-[9.5px] opacity-70">{label}</span>
      <span>{value}</span>
    </span>
  );
}

function EmptyState({ isDark, bg }) {
  return (
    <Card className={`border-dashed border-2 ${isDark ? "border-slate-700 bg-slate-900/40" : "border-slate-300 bg-slate-50/60"}`}
          data-testid="hi-empty-state">
      <CardContent className="p-8 text-center">
        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-600"
            }`}>
          <Brain className="w-8 h-8" />
        </div>
        <h3 className={`text-base font-bold mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
          {bg ? "Започни да изграждаш своя личен здравен граф" : "Start building your personal health graph"}
        </h3>
        <p className={`text-sm mb-5 max-w-md mx-auto leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          {bg
            ? "Запазвай важни анализи, любими храни, лабораторни данни — Nutramilo ще открие връзките между храна, симптоми и резултати с времето."
            : "Save important analyses, favorite foods, lab data — Nutramilo will discover connections between food, symptoms and results over time."}
        </p>
        <div className="flex flex-wrap gap-2 justify-center">
          <Link to="/dashboard/diary">
            <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white">
              <Flame className="w-4 h-4 mr-1.5" />
              {bg ? "Анализирай хранене" : "Analyze a meal"}
            </Button>
          </Link>
          <Link to="/dashboard/analyses">
            <Button variant="outline">
              <FlaskConical className="w-4 h-4 mr-1.5" />
              {bg ? "Качи лабораторни" : "Upload labs"}
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default function HealthIntelligencePage() {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const locale = bg ? bgLocale : enUS;
  // iter519 — gate female endocrine details to female users
  const userSex = (user?.sex || user?.profile?.sex || "").toLowerCase();
  const isFemale = userSex === "female" || userSex === "f";

  const [summary, setSummary] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  // iter485 — explicit GDPR consent gate for Health Intelligence (Art. 9 health data)
  const [consentChecking, setConsentChecking] = useState(true);
  const [consentOpen, setConsentOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "overview";
    try { return localStorage.getItem("hi-active-tab") || "overview"; } catch { return "overview"; }
  });
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync ?tab= query param ↔ activeTab
  useEffect(() => {
    const qTab = searchParams.get("tab");
    if (qTab && qTab !== activeTab) setActiveTab(qTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (activeTab !== searchParams.get("tab")) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    try { localStorage.setItem("hi-active-tab", activeTab); } catch {}
  }, [activeTab]);

  // iter485 — Fetch consent state on mount. If not granted, show modal and
  // block all HI data loading until user explicitly opts in (Art. 9 GDPR).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/health-intelligence/profile`);
        const granted = !!(r.data?.consent && r.data.consent.granted === true);
        if (cancelled) return;
        setConsentOpen(!granted);
      } catch {
        if (!cancelled) setConsentOpen(true);  // err on safe side
      } finally {
        if (!cancelled) setConsentChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, listRes] = await Promise.all([
        axios.get(`${API}/health-intelligence/summary?days=30`),
        axios.get(`${API}/health-intelligence/snapshots?limit=50`),
      ]);
      setSummary(sumRes.data);
      setSnapshots(listRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при зареждане" : "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [API, bg]);

  useEffect(() => {
    // iter485 — wait until consent check + consent granted before loading data
    if (consentChecking || consentOpen) return;
    fetchAll();
  }, [fetchAll, consentChecking, consentOpen]);

  const handleDelete = async (id) => {
    if (!window.confirm(bg ? "Изтрий този snapshot?" : "Delete this snapshot?")) return;
    try {
      await axios.delete(`${API}/health-intelligence/snapshot/${id}`);
      setSnapshots((arr) => arr.filter((s) => s.id !== id));
      toast.success(bg ? "Изтрит" : "Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при изтриване" : "Failed to delete"));
    }
  };

  const filtered = filter === "all"
    ? snapshots
    : snapshots.filter((s) => s.snapshot_type === filter);

  const typeCounts = summary?.totals?.by_type || {};
  const totalSnaps = summary?.totals?.snapshots_total || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="health-intelligence-page">
      <MobileTopBar title={bg ? "Здравна карта" : "Health Intelligence"} fallbackTo="/dashboard" testid="hi-mobile-topbar" />
      <MedsSummaryStrip isDark={isDark} />
      {/* iter485 — Explicit GDPR consent modal (Art. 9 health data gate) */}
      <HealthIntelligenceConsentModal
        open={consentOpen}
        apiBase={API}
        language={bg ? "bg" : "en"}
        isDark={isDark}
        onAccept={() => { setConsentOpen(false); fetchAll(); }}
        onCancel={() => { window.location.href = "/dashboard"; }}
      />
      {/* Header */}
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg`}>
              <Brain className="w-5 h-5" />
            </div>
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              Health Intelligence
            </h1>
          </div>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {bg
              ? "Личната Ви здравна интелигентност — съберете и проследявайте данните си с времето."
              : "Your personal Health Intelligence — capture and track your data over time."}
          </p>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-700"
              }`} data-testid="hi-total-count">
            {totalSnaps} {bg ? "snapshots" : "snapshots"}
          </div>
        </div>
      </header>

      {/* Всички свързани модули на едно място */}
      <div className={`mb-6 p-3 rounded-2xl border ${
          isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-slate-200"}`}
        data-testid="hi-module-rail">
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-2">
          {bg ? "Свързани модули" : "Connected modules"}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { to: "/health-intelligence/breast", label: bg ? "Гърда (BHI)" : "Breast (BHI)", id: "bhi" },
            { to: "/health-intelligence/dental", label: bg ? "Дентално (DHI)" : "Dental (DHI)", id: "dhi" },
            { to: "/dashboard/kidney-intelligence", label: bg ? "Бъбреци (KHI)" : "Kidney (KHI)", id: "khi" },
            { to: "/dashboard/stress-twin", label: bg ? "Кортизол" : "Cortisol", id: "cortisol" },
            { to: "/dashboard/nutrition-lab", label: "Nutrition Lab", id: "nutrition" },
            { to: "/dashboard/mindbody-lab", label: "Mind & Body Lab", id: "mindbody" },
            { to: "/dashboard/diary#plate-simulator-anchor", label: bg ? "Симулатор на чинията" : "Plate Simulator", id: "plate" },
            { to: "/dashboard/profile", label: bg ? "Моите данни" : "My Data", id: "profile" },
            { to: "/twins/network", label: bg ? "Жива физиологична мрежа" : "Physiology network", id: "network" },
            { to: "/dashboard/hematology-twin", label: bg ? twinLabel("hematology") : "Hematology intelligence", id: "hematology" },
            { to: "/dashboard/neuro-twin", label: bg ? twinLabel("neuro") : "Neuro intelligence", id: "neuro" },
            { to: "/dashboard/male-health-twin", label: bg ? "Мъжки Health Twin" : "Male Health Twin", id: "male" },
          ].map((m) => (
            <Link key={m.id} to={m.to} data-testid={`hi-to-${m.id}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold border transition ${
                isDark ? "bg-violet-500/10 text-violet-200 border-violet-500/30 hover:bg-violet-500/20"
                       : "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100"}`}>
              {m.label} <ArrowRight className="w-3 h-3 opacity-60" />
            </Link>
          ))}
        </div>
      </div>

      {/* iter504 — How to start (consistent onboarding card) */}
      <HowToStartCard
        storageKey="nutramilo_howto_health_intelligence"
        isDark={isDark}
        accent="from-violet-500 to-fuchsia-500"
        testid="howto-health-intelligence"
        title={bg ? "Как работят здравните раздели" : "How Health Intelligence works"}
        subtitle={bg
          ? "Снимка или документ от лаборатория → извеждаме посоката във времето."
          : "Photo/PDF of a lab → AI extracts trends over time."}
        benefits={bg
          ? ["Разчитане на снимка", "Без запазване на образа (GDPR)", "Посока във времето", "Снимки на състоянието"]
          : ["AI Vision OCR", "Zero-retention (GDPR)", "Time-series trends", "Snapshots timeline"]}
        steps={bg ? [
          { title: "1. Прегледайте съгласието за поверителност",
            body: "Вижте какво изпращаме за разчитане (данни без име) и какво НЕ изпращаме — ЕГН, имена, лекарства." },
          { title: "2. Качете лабораторно изследване",
            body: "Документът се чете при нас. Снимката се разчита, след като личните данни бъдат заличени." },
          { title: "3. Запазете снимка на състоянието",
            body: "Сегашните Ви DIL, NIS, тегло, талия и VAIS се запазват в личната Ви здравна мрежа." },
          { title: "4. Вижте посоката",
            body: "След две-три такива снимки ще видите как се променя обмяната Ви във времето." },
        ] : [
          { title: "1. Review the Privacy Consent",
            body: "See what we send to AI (pseudonymized) and what we DON'T — IDs, names, medications." },
          { title: "2. Upload a lab",
            body: "PDF is parsed locally (no image to OpenAI). Photo → AI Vision with PII scrubbing." },
          { title: "3. Save a snapshot",
            body: "Your current DIL/NIS/weight/waist/VAIS are stored in your personal health graph." },
          { title: "4. See the trends",
            body: "After 2-3 snapshots you'll see how your metabolic situation changes over time." },
        ]}
      />

      {/* GDPR notice — Phase 1: light banner; Phase 3 ще има explicit consent dialog */}
      <div className={`p-3 rounded-xl flex items-start gap-3 ${
            isDark ? "bg-amber-500/10 border border-amber-500/30" : "bg-amber-50 border border-amber-200"
          }`}>
        <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDark ? "text-amber-300" : "text-amber-700"}`} />
        <p className={`text-[11.5px] leading-relaxed ${isDark ? "text-amber-100" : "text-amber-900"}`}>
          {bg
            ? "Тук се запазват само данни, които ТИ изрично избираш да запишеш (натискане на ⭐). Данните са криптирани, видими са само за теб и могат да бъдат изтрити по всяко време. Не съхраняваме данни без твоето съгласие."
            : "Only data YOU explicitly choose to save (via ⭐) is stored here. Data is encrypted, visible only to you, and can be deleted at any time. We do not store anything without your consent."}
        </p>
      </div>

      {/* KPI hero — 4 metabolic + 4 body */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className={`w-8 h-8 animate-spin ${isDark ? "text-violet-400" : "text-violet-600"}`} />
        </div>
      ) : (
        <>
          {/* === HERO row — always visible at top (engagement + clinic driver) === */}
          {summary?.hi_score && (
            <HealthIntelligenceScoreRing score={summary.hi_score} isDark={isDark} bg={bg} />
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <WeeklyReportCard />
            <ShareWithDoctorCard />
          </div>

          {/* === Health Hub Compass — visual map of all intelligence modules === */}
          <HealthHubCompass lang={bg ? "bg" : "en"} isDark={isDark} />

          {/* === Breast Health Intelligence — visible for all (iter689; men too) === */}
          {true && (
            <Link
              to="/health-intelligence/breast"
              data-testid="hi-breast-intelligence-card"
              className={`block rounded-2xl border p-4 sm:p-5 transition-all hover:scale-[1.005] hover:shadow-lg ${
                isDark
                  ? "bg-gradient-to-r from-pink-500/10 via-rose-500/5 to-purple-500/10 border-pink-500/30"
                  : "bg-gradient-to-r from-pink-50 via-rose-50 to-purple-50 border-pink-200"
              }`}
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center shrink-0 ${
                  isDark ? "bg-pink-500/20" : "bg-pink-100"
                }`}>
                  <HeartPulse className={`w-6 h-6 sm:w-7 sm:h-7 ${isDark ? "text-pink-300" : "text-pink-600"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="text-base sm:text-lg font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                      Breast Health Intelligence
                    </h3>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      isDark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700"
                    }`}>NEW</span>
                  </div>
                  <p className={`text-xs sm:text-sm leading-snug ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    {bg
                      ? "Образователен дигитален близнак — 5 тъканни среди, метаболитна оценка и лични мерки"
                      : "Educational digital twin — 5 tissue environments, metabolic intelligence, personalised interventions"}
                  </p>
                </div>
                <ArrowRight className={`w-5 h-5 shrink-0 ${isDark ? "text-pink-300" : "text-pink-600"}`} />
              </div>
            </Link>
          )}

          {/* === TABS — 4 logical user journeys === */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="hi-tabs">
            {/* iter1090 — sticky tabs on mobile: остават под MobileTopBar */}
            <TabsList
              className={`flex w-full h-auto p-1 rounded-2xl gap-1 overflow-x-auto justify-start sm:justify-center sticky top-[52px] md:static z-30 backdrop-blur-xl ${
                isDark ? "bg-slate-900/80 border border-slate-800" : "bg-slate-100/90 border border-slate-200"
              }`}
            >
              <TabsTrigger value="overview" data-testid="hi-tab-overview"
                className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold">
                <LayoutDashboard className="w-3.5 h-3.5" />
                {bg ? "Преглед" : "Overview"}
              </TabsTrigger>
              <TabsTrigger value="body" data-testid="hi-tab-body"
                className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold">
                <HeartPulse className="w-3.5 h-3.5" />
                {bg ? "Тяло и Лаборатория" : "Body & Labs"}
              </TabsTrigger>
              <TabsTrigger value="nutrition" data-testid="hi-tab-nutrition"
                className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold">
                <Utensils className="w-3.5 h-3.5" />
                {bg ? "AI Меню" : "AI Menu"}
              </TabsTrigger>
              <TabsTrigger value="profile" data-testid="hi-tab-profile"
                className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold">
                <User className="w-3.5 h-3.5" />
                {bg ? "Профил и Връзки" : "Profile & Links"}
              </TabsTrigger>
              <TabsTrigger value="snapshots" data-testid="hi-tab-snapshots"
                className="flex-1 min-w-[120px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold">
                <Bookmark className="w-3.5 h-3.5" />
                {bg ? "Snapshots" : "Snapshots"}
              </TabsTrigger>
            </TabsList>

            {/* === Tab 1: Overview — daily metabolic snapshot === */}
            <TabsContent value="overview" className="mt-4 space-y-5" data-testid="hi-tab-content-overview">
              <CollapsibleSection storageKey="metabolic" title={bg ? "Метаболитен статус (последни 30 дни)" : "Metabolic status (last 30 days)"}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <KpiCard icon={Flame} label={bg ? "DIL — Инсулинов товар" : "DIL — Insulin Load"}
                           value={summary?.metabolic?.dil_avg}
                           unit="g" color="from-orange-500 to-amber-500"
                           isDark={isDark} testId="hi-kpi-dil-avg"
                           tooltip={bg ? "DIL дневен БЮДЖЕТ (Nutramilo NIS v1.1.9).\nТова е твоята лична цел: 45-65g/ден.\n\nЗабележка: 'DIL дневен' в AI менюто е ОЦЕНКА (диапазон) от планираните хранения — не фиксиран бюджет." : "DIL daily BUDGET (Nutramilo NIS v1.1.9). Your personal target: 45-65g/day. Menu DIL is an estimated range, not a fixed budget."}
                           hint={summary?.metabolic?.meal_snapshots_count
                                  ? `${summary.metabolic.meal_snapshots_count} ${bg ? "хранения" : "meals"}`
                                  : (bg ? "Няма данни" : "No data")}
                           computeTo="/dashboard/diary#plate-simulator-anchor"
                           computeLabel={bg ? "Анализирай чиния" : "Analyze plate"} />
                  <KpiCard icon={Activity} label={bg ? "NIS — Инсулинов отговор" : "NIS — Insulin Response"}
                           value={summary?.metabolic?.nis_avg_pct}
                           unit="%" color="from-violet-500 to-fuchsia-500"
                           isDark={isDark} testId="hi-kpi-nis-avg"
                           tooltip={bg ? "NIS = Nutramilo Insulin Score v1.1.9. Инсулинов отговор спрямо чиста глюкоза (=100%). <35% оптимално · 35-55% умерен · >55% висок." : "Nutramilo Insulin Score v1.1.9"}
                           hint={bg ? "Средно за последните хранения" : "Avg across recent meals"}
                           computeTo="/dashboard/diary#plate-simulator-anchor"
                           computeLabel={bg ? "Анализирай чиния" : "Analyze plate"} />
                  <KpiCard icon={Flame} label={bg ? "Възпаление (UIS)" : "Inflammation (UIS)"}
                           value={summary?.metabolic?.inflammation_avg_pct}
                           unit="%" color="from-amber-500 to-rose-500"
                           isDark={isDark} testId="hi-kpi-uis"
                           tooltip={bg ? "UIS = Unified Inflammation Score, peer-reviewed рамка (EDIP·DII·NEAP·ω). 0% = противовъзпалително · 100% = провъзпалително." : "Unified Inflammation Score (EDIP·DII·NEAP·ω)"}
                           hint={bg ? "0=противо- · 100=провъзпалително" : "0=anti- · 100=pro-inflammatory"}
                           computeTo="/dashboard/diary#plate-simulator-anchor"
                           computeLabel={bg ? "Анализирай чиния" : "Analyze plate"} />
                  <KpiCard icon={Sparkles} label={bg ? "Сигнал за ситост" : "Satiety signal"}
                           value={summary?.metabolic?.satiety_avg}
                           unit="/100" color="from-emerald-500 to-teal-500"
                           isDark={isDark} testId="hi-kpi-satiety"
                           tooltip={bg ? "Satiety Index 0-100. Оценява колко устойчиво храненето засища (фибри + протеин + обем + DIL модулатори)." : "Satiety Index 0-100"}
                           hint={bg ? "Колко добре те засища" : "How well meals satiate you"}
                           computeTo="/dashboard/diary#plate-simulator-anchor"
                           computeLabel={bg ? "Анализирай чиния" : "Analyze plate"} />
                </div>
              </CollapsibleSection>

              <CollapsibleSection storageKey="trends-favorites" title={bg ? "Тенденции и любими храни" : "Trends & favorites"}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <MealHistoryTrends isDark={isDark} bg={bg} />
                  <FavoriteFoodsList isDark={isDark} bg={bg} />
                </div>
                {/* iter462 Phase 6 — Pinned Nutrition Lab foods */}
                <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <FoodLabRecommendedTile isDark={isDark} bg={bg} />
                  <Col3TrackerTile isDark={isDark} bg={bg} />
                </div>
              </CollapsibleSection>
            </TabsContent>

            {/* === Tab 2: Body & Labs — physical measurements and lab data === */}
            <TabsContent value="body" className="mt-4 space-y-5" data-testid="hi-tab-content-body">
              <CollapsibleSection storageKey="body" title={bg ? "Тяло" : "Body"}>
                <div id="body-measurements" className="scroll-mt-24">
                  <BodyMeasurementsCard summary={summary} onSaved={fetchAll} />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                  <KpiCard icon={Scale} label={bg ? "Тегло" : "Weight"}
                           value={summary?.body?.weight_kg}
                           unit="kg" color="from-sky-500 to-cyan-500"
                           isDark={isDark} testId="hi-kpi-weight"
                           hint={summary?.body?.weight_kg ? null : (bg ? "Попълни по-горе" : "Fill above")}
                           computeTo="/dashboard/health-intelligence?tab=body#body-measurements"
                           computeLabel={bg ? "Въведи" : "Enter"} />
                  <KpiCard icon={Ruler} label={bg ? "Талия" : "Waist"}
                           value={summary?.body?.waist_cm}
                           unit="cm" color="from-indigo-500 to-violet-500"
                           isDark={isDark} testId="hi-kpi-waist"
                           hint={summary?.body?.waist_cm ? null : (bg ? "Попълни по-горе" : "Fill above")}
                           computeTo="/dashboard/health-intelligence?tab=body#body-measurements"
                           computeLabel={bg ? "Въведи" : "Enter"} />
                  <KpiCard icon={Scale} label={bg ? "VAIS" : "VAIS"}
                           value={summary?.body?.vais_score}
                           unit="" color="from-rose-500 to-pink-500"
                           isDark={isDark} testId="hi-kpi-vais"
                           tooltip={bg ? "VAIS = Visceral Adiposity Index Score. Комбинира BMI, талия, ханш и липиди в единна оценка на висцералния мастен товар." : "Visceral Adiposity Index Score"}
                           hint={summary?.body?.vais_score != null
                                  ? (summary?.body?.vais_band || null)
                                  : (bg ? "Не е изчислено" : "Not computed")}
                           computeTo="/dashboard/analyses#vais-anchor"
                           computeLabel={bg ? "Изчисли VAIS" : "Compute VAIS"} />
                  <KpiCard icon={FlaskConical}
                           label={bg ? "Лабораторни" : "Lab panels"}
                           value={summary?.labs?.panels_saved}
                           unit="" color="from-emerald-500 to-cyan-500"
                           isDark={isDark} testId="hi-kpi-labs"
                           tooltip={bg ? "Брой запазени лабораторни панели. Всеки панел добавя +25 точки 'дълбочина' към HI Score." : "Saved lab panels. Each adds +25 depth points to HI Score."}
                           hint={(summary?.labs?.panels_saved ?? 0) === 0 ? (bg ? "Попълни по-долу" : "Fill below") : null}
                           computeTo="/dashboard/health-intelligence?tab=body#quick-lab"
                           computeLabel={bg ? "Добави анализ" : "Add lab"} />
                </div>
                <div id="quick-lab" className="mt-3 scroll-mt-24">
                    <QuickLabEntryCard onSaved={fetchAll} />
                </div>
                <div className="mt-2 flex justify-end">
                  <SaveToPmosButton
                    module="health_intelligence"
                    bg={bg}
                    payload={{
                      hi_score: summary?.hi_score,
                      vais_score: summary?.body?.vais_score,
                      vais_band: summary?.body?.vais_band,
                      panels_saved: summary?.labs?.panels_saved,
                      snapshot_date: new Date().toISOString().slice(0, 10),
                    }}
                    testId="hi-save-to-pmos"
                  />
                </div>
              </CollapsibleSection>

              <CollapsibleSection storageKey="aggregated" title={bg ? "Здравни оценки и фенотип" : "Health scores & phenotype"}>
                <AggregatedHealthScoresCard />
              </CollapsibleSection>

              {/* iter460 — Lactation Coach entry (only renders for eligible users) */}
              <LactationCoachEntryCard />

              <CollapsibleSection storageKey="pathways" title={bg ? "Метаболитни пътища" : "Metabolic pathways"}>
                <MetabolicPathwaysCard />
              </CollapsibleSection>
            </TabsContent>

            {/* === Tab 3: AI Menu & Nutrition === */}
            <TabsContent value="nutrition" className="mt-4 space-y-5" data-testid="hi-tab-content-nutrition">
              {/* iter942 — Task 3: Единен Nutrition → DIL → Food Optimizer поток */}
              <NutritionDilFoodOptimizerCard />

              <CollapsibleSection storageKey="menu" title={bg ? "Холистично меню от AI" : "Holistic AI menu"}>
                <PersonalizedMenuCard />
              </CollapsibleSection>

              <CollapsibleSection storageKey="composer" defaultOpen={false} title={bg ? "Композирай ястие · буфериращ AI анализ" : "Compose meal · buffering AI analysis"}>
                <MealComposerCard />
              </CollapsibleSection>

              <CollapsibleSection storageKey="cycle" defaultOpen={false} title={bg ? "Cycle-Aware хранене · менструален цикъл" : "Cycle-Aware nutrition"}>
                <CycleAwareCard />
              </CollapsibleSection>

              <CollapsibleSection storageKey="restaurant" defaultOpen={false} title={bg ? "Restaurant Mode · AI избор от менюто" : "Restaurant Mode · AI menu picker"}>
                <RestaurantModeCard />
              </CollapsibleSection>

              <CollapsibleSection storageKey="family" defaultOpen={false} title={bg ? "Family Meal Sync · семейно планиране" : "Family Meal Sync"}>
                <FamilyMealCard />
              </CollapsibleSection>
            </TabsContent>

            {/* === Tab 4: Profile, Lifestyle & Connections === */}
            <TabsContent value="profile" className="mt-4 space-y-5" data-testid="hi-tab-content-profile">
              <CollapsibleSection storageKey="personal-health" title={bg ? "Личен здравен профил" : "Personal health profile"}>
                <PersonalHealthManager />
              </CollapsibleSection>

              <CollapsibleSection storageKey="lifestyle" defaultOpen={false} title={bg ? "Начин на живот — спорт и психика" : "Lifestyle — sport & psych"}>
                <LifestyleTracker />
              </CollapsibleSection>

              <CollapsibleSection storageKey="connections" defaultOpen={false} title={bg ? "AI Connection Engine — открити връзки" : "AI Connection Engine — discovered links"}>
                <AIConnectionEngineCard />
              </CollapsibleSection>

              <CollapsibleSection
                storageKey="snapshots"
                title={bg ? `Запазени snapshots (${snapshots.length})` : `Saved snapshots (${snapshots.length})`}
                rightSlot={snapshots.length > 0 ? (
                  <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => setFilter("all")}
                            data-testid="hi-filter-all"
                            className={`text-[10.5px] px-2.5 py-1 rounded-full font-bold transition-colors ${
                              filter === "all"
                                ? (isDark ? "bg-violet-500/30 text-violet-200" : "bg-violet-600 text-white")
                                : (isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200")
                            }`}>
                      {bg ? "Всички" : "All"} ({snapshots.length})
                    </button>
                    {Object.entries(typeCounts).map(([t, c]) => {
                      const meta = SNAPSHOT_TYPE_META[t];
                      if (!meta) return null;
                      return (
                        <button key={t} onClick={() => setFilter(t)}
                                data-testid={`hi-filter-${t}`}
                                className={`text-[10.5px] px-2.5 py-1 rounded-full font-bold transition-colors ${
                                  filter === t
                                    ? (isDark ? "bg-violet-500/30 text-violet-200" : "bg-violet-600 text-white")
                                    : (isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200")
                                }`}>
                          {bg ? meta.bg : meta.en} ({c})
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              >
                {snapshots.length === 0 ? (
                  <EmptyState isDark={isDark} bg={bg} />
                ) : (
                  <div className="space-y-2.5">
                    {filtered.map((snap) => (
                      <SnapshotRow key={snap.id} snap={snap} isDark={isDark} bg={bg}
                                   locale={locale} onDelete={handleDelete} isFemale={isFemale} />
                    ))}
                  </div>
                )}
              </CollapsibleSection>
            </TabsContent>

            {/* === Tab 5: BHI Snapshots — saved Breast Intelligence captures === */}
            <TabsContent value="snapshots" className="mt-4 space-y-5" data-testid="hi-tab-content-snapshots">
              <CollapsibleSection storageKey="bhi-snapshots" title={bg ? "Запазени Breast Intelligence snapshots" : "Saved Breast Intelligence snapshots"}>
                <BhiSnapshotsTab isDark={isDark} bg={bg} />
              </CollapsibleSection>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
